import { useState, type FormEvent } from 'react';
import { ArrowRight, Eye, EyeOff, LoaderCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Logo } from '../components/Logo';
import { useAuth } from '../app/AuthContext';

export function AuthPage({ mode }: { mode: 'login' | 'register' }) {
  const { login, register, enterDemo } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true); setError('');
    try {
      if (mode === 'register') await register(name, email, password);
      else await login(email, password);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not continue.');
    } finally { setBusy(false); }
  }

  return (
    <div className="auth-page">
      <div className="auth-aside">
        <Logo />
        <div><span className="eyebrow">KNOW YOUR ETA</span><h2>Preparation with a finish line.</h2><p>Turn every focused session, solved problem, and interview round into visible distance travelled toward the offer.</p></div>
        <blockquote>“An offer is not a wish. It is the output of a system.”<span>— ETAtoOffer</span></blockquote>
      </div>
      <motion.div className="auth-panel" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
        <div className="auth-mobile-logo"><Logo /></div>
        <div className="auth-form-wrap">
          <span className="eyebrow">{mode === 'register' ? 'SET YOUR ETA' : 'WELCOME BACK'}</span>
          <h1>{mode === 'register' ? 'Start moving toward the offer.' : 'Continue closing the distance.'}</h1>
          <p>{mode === 'register' ? 'Create your account. Your focused 60-day plan is ready.' : 'Your roadmap, streak, and interview pipeline are waiting.'}</p>
          <form onSubmit={submit}>
            {mode === 'register' && <label>Your name<input value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" placeholder="Ada Lovelace" minLength={2} required /></label>}
            <label>Email address<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" placeholder="you@example.com" required /></label>
            <label>Password<div className="password-field"><input type={show ? 'text' : 'password'} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={mode === 'register' ? 'new-password' : 'current-password'} placeholder={mode === 'register' ? 'At least 8 characters' : 'Your password'} minLength={8} required /><button type="button" onClick={() => setShow(!show)}>{show ? <EyeOff /> : <Eye />}</button></div></label>
            {error && <div className="form-error">{error}</div>}
            <button className="button primary auth-submit" disabled={busy}>{busy ? <LoaderCircle className="spin" /> : <>{mode === 'register' ? 'Create my account' : 'Sign in'} <ArrowRight size={18} /></>}</button>
          </form>
          <div className="or"><span>or</span></div>
          <button className="button ghost auth-submit" onClick={enterDemo}>Explore with demo data</button>
          <p className="auth-switch">{mode === 'register' ? 'Already have an account?' : 'New to ETAtoOffer?'} <Link to={mode === 'register' ? '/login' : '/register'}>{mode === 'register' ? 'Sign in' : 'Create an account'}</Link></p>
        </div>
      </motion.div>
    </div>
  );
}
